import { getTeamShieldUrl } from '../urls';
import type { Bbox, LogoVsLogoTeam, ShieldVariant } from './types';
import { VISITOR_SHIELDS } from './visitor-shields.generated';

export const bbox = (x: number, y: number, w: number, h: number): Bbox => ({ x, y, w, h });

// Plain hex when local & visitor match, [local, visitor] tuple when they differ.
export type ColorSpec = string | readonly [string, string];

// Plain filename when local & visitor use the same shield, [local, visitor] tuple when they differ.
export type ShieldSpec = string | readonly [string, string];

export const LIGHT = ['#DDDDDD', '#FFFFFF'] as const;
// Generic stub palette used by teams whose real colors ESPN hasn't sent yet.
export const STUB = ['#6A001C', '#003779'] as const;

export const team = (value: string, label: string, variants: ShieldVariant[]): LogoVsLogoTeam => ({
  value,
  label,
  variants
});

// Builds the variant/uniform helpers with the league id pre-applied.
export const leagueBuilder = (leagueId: string) => {
  const variant = (color: ColorSpec, shield: ShieldSpec): ShieldVariant => {
    const [hex, vHex] = typeof color === 'string' ? [color, color] : [color[0], color[1]];
    const [file, explicitVFile] = typeof shield === 'string' ? [shield, undefined] : [shield[0], shield[1]];
    // Explicit tuple wins; otherwise fall back to the auto-discovered manifest.
    const vFile = explicitVFile ?? (VISITOR_SHIELDS.has(`${leagueId}/${file}`) ? `${file}-visitor` : undefined);
    return {
      hex,
      visitorHex: vHex === hex ? undefined : vHex,
      shieldUrl: getTeamShieldUrl(leagueId, file),
      visitorShieldUrl: vFile ? getTeamShieldUrl(leagueId, vFile) : undefined
    };
  };

  const uniform = (shield: ShieldSpec, c1: ColorSpec, c2: ColorSpec): ShieldVariant[] => [
    variant(c1, shield),
    variant(c2, shield)
  ];

  const stubUniform = (shield: ShieldSpec): ShieldVariant[] => uniform(shield, STUB[0], STUB[1]);

  // Variante que aparece SOLO en el selector visitante.
  const visitorOnly = (color: ColorSpec, shield: ShieldSpec): ShieldVariant => ({
    ...variant(color, shield),
    visitorOnly: true
  });

  // Variante que aparece SOLO en el selector local.
  const localOnly = (color: ColorSpec, shield: ShieldSpec): ShieldVariant => ({
    ...variant(color, shield),
    localOnly: true
  });

  return { variant, uniform, stubUniform, visitorOnly, localOnly };
};
